/** Measuring tool helpers: distance/angle between two points, node snapping and angle constraint. */
import { transformShape } from '../../engine/model/geometry';
import type { EmbObject } from '../../engine/model/types';
import type { Vec } from '../../engine/geometry/vec';
import { nodePaths } from './shapes';

export interface Measurement {
  length: number;
  dx: number;
  dy: number;
  /** Angle in degrees, 0° = to the right, counter-clockwise positive (y axis points down on screen). */
  angle: number;
}

export function measure(a: Vec, b: Vec): Measurement {
  const dx = b.x - a.x, dy = b.y - a.y;
  let angle = (Math.atan2(-dy, dx) * 180) / Math.PI;
  if (angle <= -180 + 1e-9) angle += 360;
  return { length: Math.hypot(dx, dy), dx, dy, angle: Object.is(angle, -0) ? 0 : angle };
}

/** Moves b so that the direction a→b is a multiple of `stepDeg`, keeping the length. */
export function constrainAngle(a: Vec, b: Vec, stepDeg = 15): Vec {
  const len = Math.hypot(b.x - a.x, b.y - a.y);
  if (len === 0) return { ...b };
  const step = (stepDeg * Math.PI) / 180;
  const ang = Math.round(Math.atan2(b.y - a.y, b.x - a.x) / step) * step;
  return { x: a.x + Math.cos(ang) * len, y: a.y + Math.sin(ang) * len };
}

/** Nearest node (in design coordinates) of a visible object within `tol` mm, or null. */
export function snapToNode(p: Vec, objects: readonly EmbObject[], tol: number): Vec | null {
  let best: Vec | null = null;
  let bestD = tol;
  for (const o of objects) {
    if (!o.visible) continue;
    for (const ref of nodePaths(transformShape(o.shape, o.transform))) {
      for (const n of ref.nodes) {
        const d = Math.hypot(n.x - p.x, n.y - p.y);
        if (d <= bestD) {
          bestD = d;
          best = { x: n.x, y: n.y };
        }
      }
    }
  }
  return best;
}
